import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";

const starter = `/**
 * Run tasks with at most \`limit\` running at any moment.
 *
 * @param tasks - functions that each return a promise
 * @param limit - the most that may be in flight at once
 * @returns the resolved values, in the SAME ORDER as \`tasks\`
 */
async function pool<T>(tasks: (() => Promise<T>)[], limit: number): Promise<T[]> {
  // TODO
  return [];
}
`;

const solution = `async function pool<T>(tasks: (() => Promise<T>)[], limit: number): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let next = 0;

  // Each worker pulls the next index off a shared counter and runs it, rather
  // than being handed a fixed slice. That is what makes this a pool and not
  // batching: a worker that finishes early starts the next task immediately
  // instead of idling until the slowest of its batch is done.
  async function worker(): Promise<void> {
    for (;;) {
      const index = next++;
      if (index >= tasks.length) return;
      // Written to the index it came from, so completion order never affects
      // the order of the returned array.
      results[index] = await tasks[index]();
    }
  }

  const size = Math.max(1, Math.min(limit, tasks.length));
  await Promise.all(Array.from({ length: size }, () => worker()));
  return results;
}`;

const harness = HARNESS_KIT + `
if (typeof pool !== "function") {
  throw new Error("pool is not defined. Keep the function name as given.");
}

// A promise you resolve by hand, so a test controls exactly when each task
// finishes and nothing depends on real time.
function __deferred() {
  let resolve, reject;
  const promise = new Promise(function (res, rej) { resolve = res; reject = rej; });
  return { promise: promise, resolve: resolve, reject: reject };
}

// Lets the microtask queue drain so pending .then callbacks have run.
function __tick() { return Promise.resolve().then().then().then(); }

// Tracks how many tasks are running at once and the peak reached.
function __tracker() {
  const t = { running: 0, peak: 0, started: [], gates: [] };
  t.task = function (id, value) {
    return function () {
      t.running++;
      t.peak = Math.max(t.peak, t.running);
      t.started.push(id);
      const d = __deferred();
      t.gates.push({ id: id, done: function () { t.running--; d.resolve(value); } });
      return d.promise;
    };
  };
  t.finish = function (id) {
    const g = t.gates.filter(function (x) { return x.id === id; })[0];
    if (g) g.done();
    return __tick();
  };
  t.finishAll = async function () {
    while (t.gates.some(function (g) { return !g.settled; })) {
      const g = t.gates.filter(function (x) { return !x.settled; })[0];
      if (!g) break;
      g.settled = true;
      g.done();
      await __tick();
    }
  };
  return t;
}

const __immediate = function (v) { return function () { return Promise.resolve(v); }; };

const __tests = [
  {
    name: "resolves every task's value",
    about: "The basic contract before any concurrency is involved.",
    run: async function () {
      await __expectExactAsync("pool([1,2,3], 2)", function () {
        return pool([__immediate(1), __immediate(2), __immediate(3)], 2);
      }, [1, 2, 3]);
    }
  },
  {
    name: "an empty task list resolves to an empty array",
    about: "No tasks is not an error, and must not hang waiting for a worker that never runs.",
    run: async function () {
      await __expectExactAsync("pool([], 3)", function () { return pool([], 3); }, []);
    }
  },
  {
    name: "results come back in input order, not completion order",
    hidden: true,
    about: "The single most common failure. Pushing results as they settle gives you completion order, which for a pool is effectively random. Write each result to the index it came from.",
    run: async function () {
      const first = __deferred();
      const second = __deferred();
      const tasks = [
        function () { return first.promise; },
        function () { return second.promise; }
      ];
      const running = pool(tasks, 2);
      // Finish the SECOND task first.
      second.resolve("second");
      await __tick();
      first.resolve("first");
      await __expectExactAsync("second task settles first", function () { return running; }, ["first", "second"]);
    }
  },
  {
    name: "never runs more than the limit at once",
    hidden: true,
    about: "The whole point. Starting everything and only collecting results in order satisfies ordering while hammering the thing you were rate-limiting.",
    run: async function () {
      const t = __tracker();
      const tasks = [];
      for (let i = 0; i < 8; i++) tasks.push(t.task(i, i));
      const running = pool(tasks, 3);
      await __tick();
      __assert("pool(8 tasks, limit 3)", t.peak <= 3,
        ["at most 3 tasks may be in flight", "but " + t.peak + " were running at once"]);
      await t.finishAll();
      await running;
    }
  },
  {
    name: "starts a waiting task as soon as a slot frees",
    hidden: true,
    about: "Distinguishes a pool from batching. Chunking into groups of N waits for the slowest in each group before starting any of the next, so a slot sits idle.",
    run: async function () {
      const t = __tracker();
      const tasks = [];
      for (let i = 0; i < 4; i++) tasks.push(t.task(i, i));
      const running = pool(tasks, 2);
      await __tick();
      __assert("pool(4 tasks, limit 2)", t.started.length === 2,
        ["exactly 2 should have started", "but " + t.started.length + " did"]);
      await t.finish(0);
      __assert("after one of two finishes", t.started.length === 3,
        ["the third task should start as soon as a slot frees",
         "but " + t.started.length + " tasks had started"]);
      await t.finishAll();
      await running;
    }
  },
  {
    name: "a limit larger than the task count is fine",
    hidden: true,
    about: "Spawning one worker per unit of limit hangs or wastes work when limit exceeds the number of tasks.",
    run: async function () {
      await __expectExactAsync("pool(2 tasks, limit 10)", function () {
        return pool([__immediate("a"), __immediate("b")], 10);
      }, ["a", "b"]);
    }
  },
  {
    name: "a limit of 1 runs strictly one at a time",
    hidden: true,
    about: "The degenerate case, and a useful one: serial execution with the same API.",
    run: async function () {
      const t = __tracker();
      const tasks = [];
      for (let i = 0; i < 3; i++) tasks.push(t.task(i, i));
      const running = pool(tasks, 1);
      await __tick();
      __assert("pool(3 tasks, limit 1)", t.peak === 1,
        ["only one task may run at a time", "but " + t.peak + " ran at once"]);
      await t.finishAll();
      await running;
    }
  },
  {
    name: "a rejecting task rejects the pool",
    hidden: true,
    about: "Swallowing a rejection and returning undefined hides a failure the caller must see.",
    run: async function () {
      await __expectRejects("one task rejects", function () {
        return pool([
          __immediate(1),
          function () { return Promise.reject(new Error("boom")); }
        ], 2);
      }, "a task rejected and the caller has to hear about it");
    }
  },
  {
    name: "tasks are called, not awaited as already-running promises",
    hidden: true,
    about: "The input is an array of FUNCTIONS. Accepting promises instead means every request has already been fired before the pool ever sees it, and the limit does nothing.",
    run: async function () {
      let started = 0;
      const make = function () { return function () { started++; return Promise.resolve(1); }; };
      const tasks = [make(), make(), make(), make()];
      const running = pool(tasks, 1);
      __assert("pool(4 tasks, limit 1) before awaiting", started <= 1,
        ["at most 1 task should have been invoked synchronously", "but " + started + " were"]);
      await running;
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "Chunking the input into groups of limit and awaiting each group does respect the ceiling. Ask what the other slots are doing while one slow task in the group finishes. A pool has no groups: it has workers pulling from a shared queue.",
    code: `async function pool<T>(tasks: (() => Promise<T>)[], limit: number): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let next = 0;   // the shared cursor every worker pulls from

  // ... workers, then await all of them
}`,
  },
  {
    label: "Approach",
    body: "Start limit workers. Each takes the next index off the cursor, awaits that task, writes into results at that index, and loops until the cursor runs out. Because a worker takes the next index rather than a fixed slice, one that finishes early starts work immediately instead of idling.",
    code: `async function worker(): Promise<void> {
  for (;;) {
    const index = next++;
    if (index >= tasks.length) return;
    // Written to the index it came FROM, so completion order never affects
    // the order of the returned array.
    results[index] = await tasks[index]();
  }
}

// Clamped, or a limit larger than the task count spawns idle workers.
const size = Math.max(1, Math.min(limit, tasks.length));
await Promise.all(Array.from({ length: size }, () => worker()));
return results;`,
  },
  {
    label: "The trap",
    body: "Write results by index, never push. Pushing gives you completion order, which for a pool is effectively random, and the caller has no way left to tell which result belongs to which input. The other half is that the input is an array of functions: call them inside the worker, because awaiting promises that were already created means every request fired before the pool ever saw it and the limit did nothing.",
  },
];

export const promisePool: Drill = {
  id: "promise-pool",
  track: "typescript",
  title: "Promise Pool",
  pattern: "async-concurrency",
  difficulty: "hard",
  basePoints: 300,
  timeBudgetMs: 30 * 60 * 1000,
  statement: `You have 200 files to upload and an API that falls over above about five concurrent requests. \`Promise.all\` fires all 200. Write \`pool\`, which runs them with a ceiling on how many are in flight.

\`\`\`ts
async function pool<T>(tasks: (() => Promise<T>)[], limit: number): Promise<T[]>
\`\`\`

Note the input is an array of **functions returning promises**, not promises. A promise is already running; a function has not started yet, which is the only way a limit can mean anything.

## Requirements

- **Never more than \`limit\` in flight** at any moment.
- **Results in input order**, regardless of what order they finish in.
- **Start the next task as soon as a slot frees**, not once the current group is done.
- **A rejection rejects the whole call**, like \`Promise.all\`.

## Example

\`\`\`ts
const urls = ["/a", "/b", "/c", "/d"];
const results = await pool(urls.map((u) => () => fetch(u).then((r) => r.json())), 2);
// two requests in flight at a time; results[0] is /a's response whatever finished first
\`\`\`

## Where this gets interesting

The obvious version chunks the input into groups of \`limit\` and awaits each group with \`Promise.all\`. That respects the ceiling, and it is not a pool: every group waits for its slowest member before the next one starts, so with one slow task among four you spend most of the time running one request at a time.

The other trap is order. If you \`push\` results as they resolve, you get completion order, and the caller has no way to match a result to its input.`,
  hints,
  starter,
  harness,
  solution,
  complexity:
    "O(n) over the tasks, with at most `limit` in flight. Constant extra memory beyond the results array: workers pull from a shared cursor rather than the input being sliced.",
};
